
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Trash2, AlertCircle, RefreshCw, Check, Info } from "lucide-react";
import { DeleteConfirmationDialog } from "@/components/knowledge/dialog-sections/DeleteConfirmationDialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const OpenAIResources = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resources, setResources] = useState<any>(null);
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<"file" | "vector_store" | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteResponse, setDeleteResponse] = useState<any>(null);
  const [successfulDeletions, setSuccessfulDeletions] = useState<Set<string>>(new Set());

  const PERSISTENT_VECTOR_STORE_ID = "vs_67e6f0abcaf88191b784bed8d1c5be18";

  const fetchResources = useCallback(async (operation = "list_all") => {
    if (isLoading) return; // Prevent multiple simultaneous requests
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("openai-resources", {
        body: { operation },
      });

      if (error) {
        throw new Error(error.message);
      }

      console.log("Resources fetched:", data);
      setResources(data);
      toast({
        title: "Resources loaded",
        description: "OpenAI resources have been fetched successfully",
      });
    } catch (error: any) {
      console.error("Error fetching OpenAI resources:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to fetch OpenAI resources: ${error.message}`,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, isLoading]); // Removed the callback dependency issue

  // Only fetch on mount, not on every render
  useEffect(() => {
    fetchResources();
  }, []); // Empty dependency array to only run on mount

  const handleDeleteClick = (type: "file" | "vector_store", id: string, name?: string) => {
    if (successfulDeletions.has(id)) {
      toast({
        title: "Already deleted",
        description: `This ${type === "file" ? "file" : "vector store"} has already been deleted`,
        variant: "default" 
      });
      return;
    }

    setDeleteType(type);
    setDeleteId(id);
    setDeleteName(name || id);
    setDeleteError(null);
    setDeleteResponse(null);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteType || !deleteId) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    setDeleteResponse(null);
    
    try {
      const operation = deleteType === "file" ? "delete_file" : "delete_vector_store";
      const idParam = deleteType === "file" ? { fileId: deleteId } : { vectorStoreId: deleteId };
      
      console.log(`Sending delete request for ${deleteType} with ID ${deleteId}`);
      
      const { data, error } = await supabase.functions.invoke("openai-resources", {
        method: "DELETE",
        body: { 
          operation,
          ...idParam
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      console.log(`Delete ${deleteType} response:`, data);

      if (!data.success) {
        throw new Error(data.error || "Operation failed");
      }

      // Check if the response indicates the deletion was confirmed
      if (!data.deleted) {
        throw new Error("Resource was not confirmed deleted by the API");
      }

      setDeleteResponse(data);
      
      // Mark as successfully deleted
      setSuccessfulDeletions(prev => new Set([...prev, deleteId]));
      
      toast({
        title: "Delete successful",
        description: `${deleteType === "file" ? "File" : "Vector store"} has been deleted`,
        variant: "default"
      });
      
      // Close dialog after short delay to show success state
      setTimeout(() => {
        setDeleteDialogOpen(false);
        // Refresh resources to update the UI
        fetchResources();
      }, 1500);
      
    } catch (error: any) {
      console.error(`Error deleting ${deleteType}:`, error);
      setDeleteError(error.message);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to delete ${deleteType}: ${error.message}`,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDialogCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteType(null);
    setDeleteId(null);
    setDeleteName(null);
    setDeleteError(null);
    setDeleteResponse(null);
  };

  // Manual refresh function
  const handleRefresh = () => {
    fetchResources("list_all");
  };

  // Function to format attributes for display
  const formatAttributes = (attributes: Record<string, any> | undefined | null) => {
    if (!attributes) return "None";
    
    return (
      <ScrollArea className="h-auto max-h-48">
        <pre className="text-xs overflow-x-auto p-2 bg-muted rounded-md whitespace-pre-wrap">
          {JSON.stringify(attributes, null, 2)}
        </pre>
      </ScrollArea>
    );
  };

  function renderFilesList(files: any[]) {
    if (!files || files.length === 0) return <p>No files found.</p>;

    return (
      <div className="space-y-4">
        {files.map((file) => {
          const isDeleted = successfulDeletions.has(file.id);
          
          return (
            <Card key={file.id} className={`p-4 ${isDeleted ? 'opacity-50' : ''}`}>
              <div className="grid grid-cols-2 gap-2">
                <div><strong>ID:</strong> {file.id}</div>
                <div><strong>Filename:</strong> {file.filename}</div>
                <div><strong>Purpose:</strong> {file.purpose}</div>
                <div><strong>Status:</strong> {file.status}</div>
                <div><strong>Created at:</strong> {new Date(file.created_at * 1000).toLocaleString()}</div>
                <div><strong>Size:</strong> {(file.bytes / 1024).toFixed(2)} KB</div>
              </div>
              <div className="mt-4 flex justify-end">
                {isDeleted ? (
                  <span className="text-green-500 flex items-center">
                    <Check className="mr-2 h-4 w-4" /> Deleted
                  </span>
                ) : (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteClick("file", file.id, file.filename)}
                    disabled={isDeleting || isLoading}
                  >
                    {isDeleting && deleteId === file.id ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="mr-2 h-4 w-4" />
                    )}
                    {isDeleting && deleteId === file.id ? "Deleting..." : "Delete File"}
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  function renderVectorStoresList(vectorStores: any[]) {
    if (!vectorStores || vectorStores.length === 0) return <p>No vector stores found.</p>;

    return (
      <div className="space-y-4">
        {vectorStores.map((store) => {
          const isDeleted = successfulDeletions.has(store.id);
          
          return (
            <Card key={store.id} className={`p-4 ${isDeleted ? 'opacity-50' : ''}`}>
              <div className="grid grid-cols-2 gap-2">
                <div><strong>ID:</strong> {store.id}</div>
                <div>
                  <strong>Name:</strong> {store.name}
                  {store.id === PERSISTENT_VECTOR_STORE_ID && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                      Persistent Store
                    </span>
                  )}
                </div>
                <div><strong>Created at:</strong> {new Date(store.created_at * 1000).toLocaleString()}</div>
                <div><strong>Last active at:</strong> {store.last_active_at ? new Date(store.last_active_at * 1000).toLocaleString() : 'N/A'}</div>
              </div>
              <div className="mt-4 flex justify-end">
                {isDeleted ? (
                  <span className="text-green-500 flex items-center">
                    <Check className="mr-2 h-4 w-4" /> Deleted
                  </span>
                ) : (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteClick("vector_store", store.id, store.name)}
                    disabled={store.id === PERSISTENT_VECTOR_STORE_ID || isDeleting || isLoading}
                    title={store.id === PERSISTENT_VECTOR_STORE_ID ? "Cannot delete persistent store" : "Delete vector store"}
                  >
                    {isDeleting && deleteId === store.id ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="mr-2 h-4 w-4" />
                    )}
                    {isDeleting && deleteId === store.id ? "Deleting..." : "Delete Vector Store"}
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  function renderVectorStoreFiles(vectorStoreFiles: any) {
    if (!vectorStoreFiles) return null;
    
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-medium mt-6">Vector Store Files</h3>
        <Accordion type="single" collapsible className="w-full">
          {Object.entries(vectorStoreFiles).map(([storeId, files]) => (
            <AccordionItem key={storeId} value={storeId}>
              <AccordionTrigger>
                Vector Store: {storeId}
                {storeId === PERSISTENT_VECTOR_STORE_ID && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                    Everest-Knowledge-Base
                  </span>
                )}
              </AccordionTrigger>
              <AccordionContent>
                {Array.isArray(files) ? (
                  files.length > 0 ? (
                    <div className="space-y-4">
                      {files.map((file) => (
                        <Card key={file.id} className="p-4">
                          <div className="grid grid-cols-2 gap-2">
                            <div><strong>File ID:</strong> {file.file_id}</div>
                            <div><strong>Status:</strong> {file.status}</div>
                            <div><strong>Created at:</strong> {new Date(file.created_at * 1000).toLocaleString()}</div>
                            <div className="col-span-2">
                              <div className="flex items-center gap-1 mb-1">
                                <strong>Attributes:</strong>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Info className="h-4 w-4 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-xs">Attributes are metadata attached to files in the vector store</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              {formatAttributes(file.attributes)}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p>No files in this vector store.</p>
                  )
                ) : (
                  <p>Error loading files: {JSON.stringify(files)}</p>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <h1 className="text-2xl font-bold mb-4">OpenAI Resources</h1>
      <p className="mb-6">
        Use this page to verify your OpenAI resources and manage files and vector stores.
        You can see the attributes attached to vector store files to verify metadata is being correctly uploaded.
      </p>
      
      <div className="flex gap-2 mb-6">
        <Button 
          onClick={handleRefresh}
          disabled={isLoading}
          variant="default"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          {isLoading ? "Refreshing..." : "Refresh Resources"}
        </Button>
      </div>

      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="all">All Resources</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="vector-stores">Vector Stores</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-500" />
              <p className="ml-2">Loading resources...</p>
            </div>
          ) : resources ? (
            <>
              <div>
                <h2 className="text-xl font-medium mb-4">Files</h2>
                {renderFilesList(resources.files)}
              </div>
              
              <div className="mt-8">
                <h2 className="text-xl font-medium mb-4">Vector Stores</h2>
                {renderVectorStoresList(resources.vectorStores)}
              </div>
              
              {renderVectorStoreFiles(resources.vectorStoreFiles)}
            </>
          ) : (
            <p>No resource data available. Click "Refresh Resources" to load data.</p>
          )}
        </TabsContent>

        <TabsContent value="files">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-500" />
              <p className="ml-2">Loading files...</p>
            </div>
          ) : resources ? (
            <>
              <h2 className="text-xl font-medium mb-4">Files</h2>
              {renderFilesList(resources.files)}
            </>
          ) : (
            <p>No file data available.</p>
          )}
        </TabsContent>

        <TabsContent value="vector-stores">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-500" />
              <p className="ml-2">Loading vector stores...</p>
            </div>
          ) : resources ? (
            <>
              <h2 className="text-xl font-medium mb-4">Vector Stores</h2>
              {renderVectorStoresList(resources.vectorStores)}
              
              {renderVectorStoreFiles(resources.vectorStoreFiles)}
            </>
          ) : (
            <p>No vector store data available.</p>
          )}
        </TabsContent>
      </Tabs>

      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleDialogCancel}
        isDeleting={isDeleting}
        title={`Confirm ${deleteType === 'file' ? 'File' : 'Vector Store'} Deletion`}
        description={`Are you sure you want to delete this ${deleteType === 'file' ? 'file' : 'vector store'}?${
          deleteError ? `\n\nError: ${deleteError}` : ''
        }`}
        itemName={deleteName || ''}
      />
    </div>
  );
};

export default OpenAIResources;
